import { css, keyframes } from '@emotion/react'

import type { SuperSource } from '@slite/search-client'

import { SOURCE_LOGOS } from './sources'

// ─── Dimensions (px) ────────────────────────────────────────────────────

export const BOX_HEIGHT = 600
const SCAN_TRAIL_HEIGHT = 180
const GRID_CELL = 15

// ─── Scan timing (seconds) ──────────────────────────────────────────────

const SCAN_DURATION_S = 3
// Line only moves during the first 60% of a cycle; the rest is an idle break.
const SCAN_TRAVEL_FRACTION = 0.6
const SCAN_TRAVEL_S = SCAN_DURATION_S * SCAN_TRAVEL_FRACTION
// 4 scans per loop so each story source lights up on its own pass.
const SCAN_COUNT = 4
const LOOP_DURATION_S = SCAN_DURATION_S * SCAN_COUNT

// ─── Insight tooltip timing + sizing ────────────────────────────────────

// Fades are fractions of a scan (not absolute seconds), so the keyframe
// percentages stay constant when SCAN_DURATION_S changes — line + tooltips
// stay in sync at any speed.
const INSIGHT_FADE_IN_FRACTION = 0.05
const INSIGHT_FADE_OUT_FRACTION = 0.08
const INSIGHT_FADE_IN_S = SCAN_DURATION_S * INSIGHT_FADE_IN_FRACTION
const INSIGHT_FADE_OUT_S = SCAN_DURATION_S * INSIGHT_FADE_OUT_FRACTION
const INSIGHT_WIDTH = 240
const INSIGHT_MAX_WIDTH = 220

// ─── Agent gaze mark ────────────────────────────────────────────────────

const ORBIT_A = 14.5
const ORBIT_B = 10.7
const ORBIT_TILT_DEG = -36
const ORBIT_CX = 17
const ORBIT_CY = 17
const GAP_DEG = 88
const GAZE_HOME_DEG = 90
const GAZE_STRENGTH = 0.72
const GAZE_MOVE_S = SCAN_DURATION_S * 0.07
const ARC_SAMPLES = 4
const RIBBON_SAMPLES = 48
const EYE_SEP_DEG = 24
const EYE_CENTER_OFFSET_DEG = -2
const EYE_INSET = 0

const toRad = (deg: number) => (deg * Math.PI) / 180
const toDeg = (rad: number) => (rad * 180) / Math.PI

function circlePoint(angleDeg: number, radius = ORBIT_A) {
  const angle = toRad(angleDeg)
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
  }
}

function orbitPoint(angleDeg: number, radius = ORBIT_A) {
  const point = circlePoint(angleDeg, radius)
  const tilt = toRad(ORBIT_TILT_DEG)
  const scaledY = point.y * (ORBIT_B / ORBIT_A)

  return {
    x: ORBIT_CX + point.x * Math.cos(tilt) - scaledY * Math.sin(tilt),
    y: ORBIT_CY + point.x * Math.sin(tilt) + scaledY * Math.cos(tilt),
  }
}

function shortestSignedDeg(deg: number) {
  return ((((deg + 180) % 360) + 360) % 360) - 180
}

function pointTransform(angleDeg: number) {
  return `translate(${pointTranslate(angleDeg)})`
}

function pointTranslate(angleDeg: number) {
  const point = orbitPoint(angleDeg, ORBIT_A - EYE_INSET)
  return `${point.x.toFixed(3)} ${point.y.toFixed(3)}`
}

function aimDelta(leftPct: number, topPct: number) {
  const alpha = Math.atan2(topPct - 50, leftPct - 50)
  const ellipseSpace = alpha - toRad(ORBIT_TILT_DEG)
  const circleAngle = toDeg(
    Math.atan2(
      ORBIT_A * Math.sin(ellipseSpace),
      ORBIT_B * Math.cos(ellipseSpace)
    )
  )

  return shortestSignedDeg(circleAngle - GAZE_HOME_DEG) * GAZE_STRENGTH
}

const atRest = {
  delta: 0,
  eyeAngle: GAZE_HOME_DEG,
}

const gazeStops = SOURCE_LOGOS.filter(
  (logo) => logo.insight && logo.scanIndex !== undefined
)
  .map((logo) => {
    const topPct = parseFloat(logo.top)
    const crossY = (topPct / 100) * BOX_HEIGHT
    const crossTime =
      (SCAN_TRAVEL_S * (BOX_HEIGHT - crossY)) / (BOX_HEIGHT + SCAN_TRAIL_HEIGHT)
    const appearAt = logo.scanIndex! * SCAN_DURATION_S + crossTime
    const visibleEnd =
      logo.scanIndex! * SCAN_DURATION_S + SCAN_DURATION_S - INSIGHT_FADE_OUT_S
    const delta = aimDelta(parseFloat(logo.left), topPct)

    return {
      appearAt,
      visibleEnd,
      delta,
      eyeAngle: GAZE_HOME_DEG + delta,
    }
  })
  .sort((a, b) => a.appearAt - b.appearAt)

function addArcMove(
  frames: { time: number; delta: number; eyeAngle: number }[],
  fromTime: number,
  toTime: number,
  fromDelta: number,
  toDelta: number
) {
  for (let i = 0; i <= ARC_SAMPLES; i += 1) {
    const progress = i / ARC_SAMPLES
    frames.push({
      time: fromTime + (toTime - fromTime) * progress,
      delta: fromDelta + (toDelta - fromDelta) * progress,
      eyeAngle: GAZE_HOME_DEG + fromDelta + (toDelta - fromDelta) * progress,
    })
  }
}

function buildGazeFrames() {
  const frames = [{ time: 0, ...atRest }]

  gazeStops.forEach((stop) => {
    addArcMove(
      frames,
      stop.appearAt - GAZE_MOVE_S,
      stop.appearAt,
      0,
      stop.delta
    )
    frames.push({
      time: stop.visibleEnd,
      delta: stop.delta,
      eyeAngle: stop.eyeAngle,
    })
    addArcMove(
      frames,
      stop.visibleEnd,
      stop.visibleEnd + GAZE_MOVE_S,
      stop.delta,
      0
    )
  })

  frames.push({ time: LOOP_DURATION_S, ...atRest })

  return frames
    .filter((frame) => frame.time >= 0 && frame.time <= LOOP_DURATION_S)
    .sort((a, b) => a.time - b.time)
}

function ribbonPath(angleDeg: number) {
  const start = angleDeg + GAP_DEG / 2
  const sweep = 360 - GAP_DEG
  return Array.from({ length: RIBBON_SAMPLES + 1 }, (_, index) => {
    const point = orbitPoint(start + (sweep * index) / RIBBON_SAMPLES)
    const command = index === 0 ? 'M' : 'L'
    return `${command} ${point.x.toFixed(3)} ${point.y.toFixed(3)}`
  }).join(' ')
}

const gazeFrames = buildGazeFrames()

function smilValues(getValue: (frame: (typeof gazeFrames)[number]) => string) {
  return gazeFrames.map(getValue).join(';')
}

const leftEyeOffset = EYE_CENTER_OFFSET_DEG + EYE_SEP_DEG / 2
const rightEyeOffset = EYE_CENTER_OFFSET_DEG - EYE_SEP_DEG / 2
const ribbonKeyTimes = gazeFrames
  .map((frame) => (frame.time / LOOP_DURATION_S).toFixed(4))
  .join(';')

export const ORBIT = {
  viewBox: '0 0 34 34',
  ribbonPath: ribbonPath(GAZE_HOME_DEG),
  ribbonPathValues: smilValues((frame) => ribbonPath(frame.eyeAngle)),
  leftEyeTransform: pointTransform(GAZE_HOME_DEG + leftEyeOffset),
  leftEyeTransformValues: smilValues((frame) =>
    pointTranslate(frame.eyeAngle + leftEyeOffset)
  ),
  rightEyeTransform: pointTransform(GAZE_HOME_DEG + rightEyeOffset),
  rightEyeTransformValues: smilValues((frame) =>
    pointTranslate(frame.eyeAngle + rightEyeOffset)
  ),
  keyTimes: ribbonKeyTimes,
  loopDuration: `${LOOP_DURATION_S}s`,
}

// ─── Scan line + grid trail ─────────────────────────────────────────────

const scanLine = keyframes`
  0% {
    transform: translateY(${BOX_HEIGHT}px);
    opacity: 1;
  }
  35% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  60%,
  100% {
    transform: translateY(-${SCAN_TRAIL_HEIGHT}px);
    opacity: 0;
  }
`

const scanTrail = keyframes`
  0% {
    -webkit-mask-position: 0 ${BOX_HEIGHT}px;
    mask-position: 0 ${BOX_HEIGHT}px;
    opacity: 0.4;
  }
  35% {
    opacity: 0.4;
  }
  50% {
    opacity: 0;
  }
  60%,
  100% {
    -webkit-mask-position: 0 -${SCAN_TRAIL_HEIGHT}px;
    mask-position: 0 -${SCAN_TRAIL_HEIGHT}px;
    opacity: 0;
  }
`

export const scanLineCss = css`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 2px;
  background: var(--saturn-orange);
  box-shadow: 0 0 8px 1px var(--saturn-orange);
  pointer-events: none;
  animation: ${scanLine} ${SCAN_DURATION_S}s linear infinite;
`

// Faint static grid + a bright trailing band that brightens the same lines.
export const baseGridCss = css`
  position: absolute;
  inset: 0;
  opacity: 0.08;
  pointer-events: none;
  background-image: linear-gradient(
      to right,
      var(--shade-30) 1px,
      transparent 1px
    ),
    linear-gradient(to bottom, var(--shade-30) 1px, transparent 1px);
  background-size: ${GRID_CELL}px ${GRID_CELL}px;
`

export const scanTrailCss = css`
  position: absolute;
  inset: 0;
  opacity: 0.4;
  pointer-events: none;
  background-image: linear-gradient(
      to right,
      var(--saturn-orange) 1px,
      transparent 1px
    ),
    linear-gradient(to bottom, var(--saturn-orange) 1px, transparent 1px);
  background-size: ${GRID_CELL}px ${GRID_CELL}px;
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 0%, transparent 100%);
  -webkit-mask-size: 100% ${SCAN_TRAIL_HEIGHT}px;
  mask-size: 100% ${SCAN_TRAIL_HEIGHT}px;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  animation: ${scanTrail} ${SCAN_DURATION_S}s linear infinite;
`

// ─── Per-source insight tooltip (timed to its own scan pass) ────────────

function buildInsightCss(scanIndex: number, topPct: number) {
  const crossY = (topPct / 100) * BOX_HEIGHT
  const crossTime =
    (SCAN_TRAVEL_S * (BOX_HEIGHT - crossY)) / (BOX_HEIGHT + SCAN_TRAIL_HEIGHT)
  const appearAt = scanIndex * SCAN_DURATION_S + crossTime
  const visibleEnd =
    scanIndex * SCAN_DURATION_S + SCAN_DURATION_S - INSIGHT_FADE_OUT_S
  const at = (t: number) => `${((t / LOOP_DURATION_S) * 100).toFixed(2)}%`
  const pop = keyframes`
    0%, ${at(appearAt)} {
      opacity: 0;
      transform: translateY(6px) scale(0.96);
    }
    ${at(appearAt + INSIGHT_FADE_IN_S)}, ${at(visibleEnd - INSIGHT_FADE_OUT_S)} {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    ${at(visibleEnd)}, 100% {
      opacity: 0;
      transform: translateY(-4px) scale(0.98);
    }
  `
  const ring = keyframes`
    0%, ${at(appearAt)} {
      opacity: 0;
    }
    ${at(appearAt + INSIGHT_FADE_IN_S)}, ${at(visibleEnd - INSIGHT_FADE_OUT_S)} {
      opacity: 1;
    }
    ${at(visibleEnd)}, 100% {
      opacity: 0;
    }
  `
  return {
    tooltip: css`
      opacity: 0;
      animation: ${pop} ${LOOP_DURATION_S}s linear infinite;
    `,
    border: css`
      opacity: 0;
      animation: ${ring} ${LOOP_DURATION_S}s linear infinite;
    `,
  }
}

export const INSIGHT_CSS = new Map<
  SuperSource,
  ReturnType<typeof buildInsightCss>
>()
SOURCE_LOGOS.forEach((logo) => {
  if (logo.insight && logo.scanIndex !== undefined) {
    INSIGHT_CSS.set(
      logo.source,
      buildInsightCss(logo.scanIndex, parseFloat(logo.top))
    )
  }
})

export const insightCardCss = css`
  position: relative;
  max-width: ${INSIGHT_MAX_WIDTH}px;
  width: ${INSIGHT_WIDTH}px;
  line-height: 1.4;
  text-align: left;
  opacity: 0;
`

// Arrow on the LEFT edge of the card, pointing into the logo on its left.
// Pair with insightAnchorRightOfLogoCss.
export const insightArrowOnLeftCss = css`
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 100%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: var(--shade-100);
  }
`

// Arrow on the RIGHT edge of the card, pointing into the logo on its right.
// Pair with insightAnchorLeftOfLogoCss.
export const insightArrowOnRightCss = css`
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 100%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-left-color: var(--shade-100);
  }
`

// Positions the tooltip to the RIGHT of the logo. Used for logos on the
// left half of the stage.
export const insightAnchorRightOfLogoCss = css`
  position: absolute;
  z-index: 3;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
`

// Positions the tooltip to the LEFT of the logo. Used for logos on the
// right half of the stage.
export const insightAnchorLeftOfLogoCss = css`
  position: absolute;
  z-index: 3;
  right: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
`
